<script lang="ts" setup>
import { ref, onMounted, computed, onBeforeMount, inject } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axiosInstance from '@/plugins/axios';
import { useToast } from 'primevue/usetoast';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';


const gF = inject('gF') as any;
const route = useRoute();
const toast = useToast();
const router = useRouter();
const evaluationCategory = ref(route.params.category || 'error');
const choosenCategoryFields = ref({})
const rootCategoryFields = ref({})
const targetLanguageId = ref(route.params.translation || 'error');
const isSaving = ref(false);
const targetLanguageCode = ref('undefined');
const rootLanguageCode = ref('undefined');
const toolbar = inject('quillToolbar') ?? [['bold']]
const exampleVisible = ref(false);

const highlightEmptyParagraphs = (content: string) => {
    return content.replace(/<p>(\s|<br>)*<\/p>/g, '<p style="background-color: yellow; width: 100%;">[Break]</p>');
};

const getTranslation = async () => {
    gF.startLoading();
    try {
        const response = await axiosInstance.get(`/translations/${targetLanguageId.value}`);
        if (evaluationCategory.value === 'summaries') {
            choosenCategoryFields.value = response.data.evaluations.summaries;
        } else if (evaluationCategory.value === 'justifications') {
            choosenCategoryFields.value = response.data.evaluations.justifications;
        } else if (evaluationCategory.value === 'result') {
            choosenCategoryFields.value = response.data.evaluations.result;
        } else if (evaluationCategory.value === 'requirements') {
            // Requirements werden im evaluations.requirements Objekt gespeichert
            choosenCategoryFields.value = response.data.evaluations.requirements || {};
        }
        console.log("response", response.data.languageCode);
        targetLanguageCode.value = response.data.languageCode;
        gF.endLoading();
    } catch (error) {
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Translation could not be loaded: ' + error,
            life: 3000,
        });
        gF.endLoading();
    }
};


const getRootTranslation = async () => {
    try {

        if (targetLanguageId.value === '670e1e32d4634dd3c3ec7478') {
            const response = await axiosInstance.get(`/translations/670fad078be48456eb53b250`);
            if (evaluationCategory.value === 'summaries') {
                rootLanguageCode.value = "de";
                rootCategoryFields.value = response.data.evaluations.summaries;
            } else if (evaluationCategory.value === 'justifications') {
                rootLanguageCode.value = "de";
                rootCategoryFields.value = response.data.evaluations.justifications;
            } else if (evaluationCategory.value === 'result') {
                rootLanguageCode.value = "de";
                rootCategoryFields.value = response.data.evaluations.result;
            } else if (evaluationCategory.value === 'requirements') {
                rootLanguageCode.value = "de";
                rootCategoryFields.value = response.data.evaluations.requirements || {};
            }
        } else {
            const response = await axiosInstance.get(`/translations/670e1e32d4634dd3c3ec7478`);
            if (evaluationCategory.value === 'summaries') {
                rootLanguageCode.value = "en";
                rootCategoryFields.value = response.data.evaluations.summaries;
            } else if (evaluationCategory.value === 'justifications') {
                rootLanguageCode.value = "en";
                rootCategoryFields.value = response.data.evaluations.justifications;
            } else if (evaluationCategory.value === 'result') {
                rootLanguageCode.value = "en";
                rootCategoryFields.value = response.data.evaluations.result;
            } else if (evaluationCategory.value === 'requirements') {
                rootLanguageCode.value = "en";
                rootCategoryFields.value = response.data.evaluations.requirements || {};
            }
        }
    } catch (error) {
        console.log(error);
    }
};

const saveEvaluationTranslation = async () => {
    gF.startLoading();
    isSaving.value = true;
    try {

        const response = await axiosInstance.put(`/translations/updateEvaluations/${targetLanguageId.value}/${evaluationCategory.value}`, {
            evaluations: choosenCategoryFields.value
        });

        toast.add({
            severity: 'success',
            summary: 'Success',
            detail: `Displayed ${evaluationCategory.value} saved successfully`,
            life: 3000
        });
        gF.endLoading();
        isSaving.value = false;
    } catch (error) {
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: `${evaluationCategory.value.charAt(0).toUpperCase() + evaluationCategory.value.slice(1)} could not be saved: ` + error,
            life: 3000
        });
        gF.endLoading();
        isSaving.value = false;
    }
};


onMounted(async () => {
    await getTranslation();
    Object.keys(rootCategoryFields.value).forEach((fieldID) => {
        if (typeof choosenCategoryFields.value[fieldID] !== 'object' || choosenCategoryFields.value[fieldID] === null) {
            choosenCategoryFields.value[fieldID] = { content: '' };
        }
    });
    await getRootTranslation();
})


</script>


<template>
    <div>
        <div class="row col-12 justify-content-center">
            <div class="col text-center mb-3">
                <Button class="w-30 p-button-tuev" label="Save" @click="saveEvaluationTranslation()"
                    iconPos="left" :loading="isSaving">
                    <template #icon>
                        <font-awesome-icon :icon="['fas', 'save']" />
                    </template>
                </Button>
            </div>
        </div>

        <Message v-if="evaluationCategory !== 'result'" severity="secondary" class="mb-5" closeable>
            <template #icon>
            </template>
            <p class="ml-2">
                To ensure that the result pages are displayed correctly, spacing needs to be included. This spacing
                (empty lines) is indicated by <span style="background-color: yellow;">[Break]</span>, as shown below.
                While
                adding your translation, please ensure that these
                breaks are also present in your translation and that both columns are visually in this regard. <br>
                To add the spacing, simply include one or more empty line(s) in your editor before your translation.<br>
                If there is no <span style="background-color: yellow;">[Break]</span> in the left column, there is no
                need to
                include a <span style="background-color: yellow;">[Break]</span> in the translation
                either. In rare cases, <span style="background-color: yellow;">[Break]</span> is needed two times.
            </p>
            <Button label="Show Example" @click="exampleVisible = true" class="p-button-tuev">
                <template #icon>
                    <font-awesome-icon :icon="['fas', 'up-right-from-square']" />
                </template>
            </Button>
        </Message>

        <Dialog header="Example" v-model:visible="exampleVisible" :modal="true" :resizable="true"
            :style="{ width: '60vw' }">
            <div class="text-center">
                <!-- As if translation are releveant this should be fixed -->
                <!-- <img src="localhost" alt="Example" class="img-fluid" /> -->
            </div>
        </Dialog>


        <Splitter style="height: auto; background-color: var(--tuev-light-blue);" class="mb-3">
            <SplitterPanel class="flex p-3 justify-content-center">
                <div class="fw-bold">{{ rootLanguageCode }}</div>
            </SplitterPanel>
            <SplitterPanel class="flex p-3  justify-content-center">
                <div class="fw-bold">{{ targetLanguageCode }}</div>
            </SplitterPanel>
            <SplitterPanel class="flex p-3 justify-content-center">
                <div class="fw-bold">Editor</div>
            </SplitterPanel>
        </Splitter>

        <div v-for="(field, fieldID) in rootCategoryFields" v-bind:key="fieldID">
            <Card class="mb-3 evaCard">
                <template #content>
                    <Splitter style="height: auto">
                        <SplitterPanel class="flex p-3 w-100">
                            <div class="w-100" v-html="highlightEmptyParagraphs(field.content)"></div>
                        </SplitterPanel>
                        <SplitterPanel class="flex p-3 w-100">
                            <div class="w-100" v-html="highlightEmptyParagraphs(
                                (typeof choosenCategoryFields[fieldID] === 'object' && choosenCategoryFields[fieldID] !== null)
                                    ? choosenCategoryFields[fieldID].content
                                    : (choosenCategoryFields[fieldID] = { content: '' }).content
                            )">
                            </div>
                        </SplitterPanel>
                        <SplitterPanel class="flex p-3">
                            <LimitedEditor :modules="{ toolbar }" style="height: 60% !important;" v-model="(typeof choosenCategoryFields[fieldID] === 'object' && choosenCategoryFields[fieldID] !== null
                                ? choosenCategoryFields[fieldID]
                                : (choosenCategoryFields[fieldID] = { content: '' })
                            ).content" class="w-100">
                                <template v-slot:toolbar>

                                </template>
                            </LimitedEditor>

                        </SplitterPanel>
                    </Splitter>

                </template>
            </Card>
        </div>
    </div>
</template>

<style scoped>
.evaCard .p-card-body {
    padding: 0px !important;
}

.p-editor-toolbar {
    display: none !important;
}
</style>